/**
 * Places, for the dropdowns that used to be free-text boxes.
 *
 * A license issued by "Fl", "fla" and "FL" is the same license, but the
 * paperwork only accepts one of them — and a passport has no issuing state at
 * all, which the form was asking for anyway.
 */

export interface Place { code: string; name: string }

/** The fifty states, DC, and the territories that issue driver licenses. */
export const US_STATES: Place[] = [
  { code: "AL", name: "Alabama" },        { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },        { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },     { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },    { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },        { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },         { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },       { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },           { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },       { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },          { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },      { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },       { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },       { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },     { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" }, { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },           { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },         { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },   { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },   { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },          { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },        { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },     { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },      { code: "WY", name: "Wyoming" },
  { code: "PR", name: "Puerto Rico" },    { code: "VI", name: "U.S. Virgin Islands" },
  { code: "GU", name: "Guam" },           { code: "AS", name: "American Samoa" },
  { code: "MP", name: "Northern Mariana Islands" },
];

export const isUsState = (code: string) =>
  US_STATES.some((s) => s.code === code.trim().toUpperCase());

export const stateName = (code: string) =>
  US_STATES.find((s) => s.code === code.trim().toUpperCase())?.name ?? code;

/**
 * Countries, for a passport.
 *
 * The ones a buyer in south-west Florida is most likely to hold are listed
 * first, then everything else alphabetically. Scrolling past Afghanistan to
 * reach Venezuela a dozen times a month is its own small tax.
 */
const COMMON = [
  { code: "US", name: "United States" },
  { code: "CU", name: "Cuba" },
  { code: "MX", name: "Mexico" },
  { code: "CO", name: "Colombia" },
  { code: "VE", name: "Venezuela" },
  { code: "HT", name: "Haiti" },
  { code: "DO", name: "Dominican Republic" },
  { code: "BR", name: "Brazil" },
  { code: "CA", name: "Canada" },
  { code: "PE", name: "Peru" },
  { code: "AR", name: "Argentina" },
  { code: "NI", name: "Nicaragua" },
  { code: "HN", name: "Honduras" },
  { code: "GT", name: "Guatemala" },
  { code: "SV", name: "El Salvador" },
];

const REST: Place[] = ([
  ["AF","Afghanistan"],["AL","Albania"],["DZ","Algeria"],["AD","Andorra"],
  ["AO","Angola"],["AG","Antigua and Barbuda"],["AM","Armenia"],["AU","Australia"],
  ["AT","Austria"],["AZ","Azerbaijan"],["BS","Bahamas"],["BH","Bahrain"],
  ["BD","Bangladesh"],["BB","Barbados"],["BY","Belarus"],["BE","Belgium"],
  ["BZ","Belize"],["BJ","Benin"],["BT","Bhutan"],["BO","Bolivia"],
  ["BA","Bosnia and Herzegovina"],["BW","Botswana"],["BN","Brunei"],["BG","Bulgaria"],
  ["BF","Burkina Faso"],["BI","Burundi"],["KH","Cambodia"],["CM","Cameroon"],
  ["CV","Cape Verde"],["CF","Central African Republic"],["TD","Chad"],["CL","Chile"],
  ["CN","China"],["KM","Comoros"],["CG","Congo"],["CD","Congo (DRC)"],
  ["CR","Costa Rica"],["CI","Côte d'Ivoire"],["HR","Croatia"],["CY","Cyprus"],
  ["CZ","Czechia"],["DK","Denmark"],["DJ","Djibouti"],["DM","Dominica"],
  ["EC","Ecuador"],["EG","Egypt"],["GQ","Equatorial Guinea"],["ER","Eritrea"],
  ["EE","Estonia"],["SZ","Eswatini"],["ET","Ethiopia"],["FJ","Fiji"],
  ["FI","Finland"],["FR","France"],["GA","Gabon"],["GM","Gambia"],
  ["GE","Georgia"],["DE","Germany"],["GH","Ghana"],["GR","Greece"],
  ["GD","Grenada"],["GN","Guinea"],["GW","Guinea-Bissau"],["GY","Guyana"],
  ["HU","Hungary"],["IS","Iceland"],["IN","India"],["ID","Indonesia"],
  ["IR","Iran"],["IQ","Iraq"],["IE","Ireland"],["IL","Israel"],
  ["IT","Italy"],["JM","Jamaica"],["JP","Japan"],["JO","Jordan"],
  ["KZ","Kazakhstan"],["KE","Kenya"],["KI","Kiribati"],["KW","Kuwait"],
  ["KG","Kyrgyzstan"],["LA","Laos"],["LV","Latvia"],["LB","Lebanon"],
  ["LS","Lesotho"],["LR","Liberia"],["LY","Libya"],["LI","Liechtenstein"],
  ["LT","Lithuania"],["LU","Luxembourg"],["MG","Madagascar"],["MW","Malawi"],
  ["MY","Malaysia"],["MV","Maldives"],["ML","Mali"],["MT","Malta"],
  ["MH","Marshall Islands"],["MR","Mauritania"],["MU","Mauritius"],["FM","Micronesia"],
  ["MD","Moldova"],["MC","Monaco"],["MN","Mongolia"],["ME","Montenegro"],
  ["MA","Morocco"],["MZ","Mozambique"],["MM","Myanmar"],["NA","Namibia"],
  ["NR","Nauru"],["NP","Nepal"],["NL","Netherlands"],["NZ","New Zealand"],
  ["NE","Niger"],["NG","Nigeria"],["KP","North Korea"],["MK","North Macedonia"],
  ["NO","Norway"],["OM","Oman"],["PK","Pakistan"],["PW","Palau"],
  ["PS","Palestine"],["PA","Panama"],["PG","Papua New Guinea"],["PY","Paraguay"],
  ["PH","Philippines"],["PL","Poland"],["PT","Portugal"],["QA","Qatar"],
  ["RO","Romania"],["RU","Russia"],["RW","Rwanda"],["KN","Saint Kitts and Nevis"],
  ["LC","Saint Lucia"],["VC","Saint Vincent and the Grenadines"],["WS","Samoa"],
  ["SM","San Marino"],["ST","São Tomé and Príncipe"],["SA","Saudi Arabia"],
  ["SN","Senegal"],["RS","Serbia"],["SC","Seychelles"],["SL","Sierra Leone"],
  ["SG","Singapore"],["SK","Slovakia"],["SI","Slovenia"],["SB","Solomon Islands"],
  ["SO","Somalia"],["ZA","South Africa"],["KR","South Korea"],["SS","South Sudan"],
  ["ES","Spain"],["LK","Sri Lanka"],["SD","Sudan"],["SR","Suriname"],
  ["SE","Sweden"],["CH","Switzerland"],["SY","Syria"],["TW","Taiwan"],
  ["TJ","Tajikistan"],["TZ","Tanzania"],["TH","Thailand"],["TL","Timor-Leste"],
  ["TG","Togo"],["TO","Tonga"],["TT","Trinidad and Tobago"],["TN","Tunisia"],
  ["TR","Türkiye"],["TM","Turkmenistan"],["TV","Tuvalu"],["UG","Uganda"],
  ["UA","Ukraine"],["AE","United Arab Emirates"],["GB","United Kingdom"],
  ["UY","Uruguay"],["UZ","Uzbekistan"],["VU","Vanuatu"],["VA","Vatican City"],
  ["VN","Vietnam"],["YE","Yemen"],["ZM","Zambia"],["ZW","Zimbabwe"],
] as [string, string][]).map(([code, name]) => ({ code, name }));

export const COUNTRIES: Place[] = [...COMMON, ...REST];

export const isCountry = (code: string) =>
  COUNTRIES.some((c) => c.code === code.trim().toUpperCase());
